import { next } from '@ember/runloop';

const VIEWPORT_GAP = 4;
const MINIMUM_OVERLAY_HEIGHT = 96;
const TOP_LAYER_CLASS = 'wysiwyg-editor-shell__top-layer-overlay';

interface FloatingOverlayAnchor {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

interface FloatingOverlayOptions {
  gap?: number;
  horizontalAlignment?: 'center' | 'start';
  restoreFocusTo?: HTMLElement;
  verticalPlacementBoundary?: Pick<FloatingOverlayAnchor, 'bottom' | 'top'>;
}

type PopoverElement = HTMLElement & {
  showPopover?: () => void;
};

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

export default class FloatingOverlayManager {
  private activeElement?: HTMLElement;
  private listeners?: AbortController;

  show(
    element: HTMLElement,
    anchor: FloatingOverlayAnchor,
    layoutAnchor: HTMLElement,
    onLayoutChange: () => void,
    options: FloatingOverlayOptions = {}
  ) {
    this.listeners?.abort();
    this.listeners = undefined;
    this.activeElement = element;

    const popoverElement = element as PopoverElement;

    if (typeof popoverElement.showPopover !== 'function') {
      element.removeAttribute('popover');
      return;
    }

    element.classList.add(TOP_LAYER_CLASS);

    if (!element.matches(':popover-open')) {
      popoverElement.showPopover();
    }

    const overlayRect = element.getBoundingClientRect();
    const gap = options.gap ?? VIEWPORT_GAP;
    const preferredLeft =
      options.horizontalAlignment === 'center'
        ? (anchor.left + anchor.right - overlayRect.width) / 2
        : anchor.left;
    const maximumLeft = Math.max(0, window.innerWidth - overlayRect.width);
    const verticalPlacementBoundary =
      options.verticalPlacementBoundary &&
      options.verticalPlacementBoundary.bottom >
        options.verticalPlacementBoundary.top
        ? options.verticalPlacementBoundary
        : {
            bottom: window.innerHeight - VIEWPORT_GAP,
            top: VIEWPORT_GAP,
          };
    const availableAbove =
      anchor.top - Math.max(verticalPlacementBoundary.top, VIEWPORT_GAP) - gap;
    const availableBelow =
      Math.min(
        verticalPlacementBoundary.bottom,
        window.innerHeight - VIEWPORT_GAP
      ) -
      anchor.bottom -
      gap;
    const shouldPlaceAbove =
      overlayRect.height > availableBelow && availableAbove > availableBelow;
    const availableHeight = shouldPlaceAbove ? availableAbove : availableBelow;
    const viewportMaximumHeight = Math.max(
      0,
      window.innerHeight - VIEWPORT_GAP * 2
    );
    // Degenerate editor-boundary measurements must leave the popup usable,
    // while short viewports still take precedence over the preferred floor.
    const maximumHeight = Math.min(
      viewportMaximumHeight,
      Math.max(MINIMUM_OVERLAY_HEIGHT, availableHeight)
    );
    const overlayHeight = Math.min(overlayRect.height, maximumHeight);
    const preferredTop = anchor.bottom + gap;
    const maximumTop = Math.max(
      VIEWPORT_GAP,
      window.innerHeight - overlayHeight - VIEWPORT_GAP
    );
    const aboveTop = anchor.top - overlayHeight - gap;
    const top = clamp(
      shouldPlaceAbove ? aboveTop : preferredTop,
      VIEWPORT_GAP,
      maximumTop
    );
    const left = clamp(preferredLeft, 0, maximumLeft);

    element.style.left = `${left}px`;
    element.style.maxHeight = `${maximumHeight}px`;

    if (
      shouldPlaceAbove &&
      aboveTop >= VIEWPORT_GAP &&
      aboveTop <= maximumTop
    ) {
      element.style.top = 'auto';
      element.style.bottom = `${window.innerHeight - anchor.top + gap}px`;
    } else {
      element.style.top = `${top}px`;
      element.style.bottom = 'auto';
    }

    const listeners = new AbortController();

    this.listeners = listeners;

    next(() => {
      if (
        this.activeElement !== element ||
        this.listeners !== listeners ||
        listeners.signal.aborted
      ) {
        return;
      }

      const { signal } = listeners;
      const handleLayoutChange = () => {
        const shouldRestoreFocus = element.contains(document.activeElement);

        onLayoutChange();

        if (shouldRestoreFocus) {
          next(() => {
            if (options.restoreFocusTo?.isConnected) {
              options.restoreFocusTo.focus({ preventScroll: true });
            }
          });
        }
      };
      let ancestor: HTMLElement | null = layoutAnchor;

      while (ancestor && ancestor !== document.body) {
        ancestor.addEventListener('scroll', handleLayoutChange, { signal });
        ancestor = ancestor.parentElement;
      }

      window.addEventListener('scroll', handleLayoutChange, { signal });
      window.addEventListener('resize', handleLayoutChange, { signal });
    });
  }

  unregister(element: HTMLElement) {
    element.style.maxHeight = '';

    if (element !== this.activeElement) {
      return;
    }

    this.listeners?.abort();
    this.listeners = undefined;
    this.activeElement = undefined;
  }
}
